import React from "react";
import styled from "styled-components";
import { isEmptyDeep } from "../../../../shared";
import { useFluidForm } from '../FormContext';
import ValidationIconSmall from '../fields/ValidationIconSmall';
import { CircleChevronLeft, CircleChevronRight } from 'lucide-react';
import { useFormInstanceSelect } from '../FormInstance.service';
import { useFormValue } from '../common';
function computeTotals(items, fluidForm) {
    let total = 0;
    for (const section of items) {
        const fields = Object.values((section.data?.value || {}));
        total += fields.length;
    }
    const completed = fluidForm.getCompletedFieldsCount();
    const percent = total === 0 ? 0 : Math.min(100, Math.round((completed / total) * 100));
    return { completed, total, percent };
}
export function SectionsProgress(props) {
    const { items, activeIndex = 0, className = "", collapsed = false, onSectionSelect, onToggleCollapse, } = props;
    const fluidForm = useFluidForm();
    const { completed, total, percent } = computeTotals(items, fluidForm);
    // Get background color from styling options
    const globalStyles = fluidForm.getAdditionalConfigValue("styleConfig", "globalStyles")?.[0];
    const backgroundColor = globalStyles?.backgroundColor || '#ffffff';
    const primaryColor = globalStyles?.primaryColor || '#f97316';
    return React.createElement("div", { className: `transition-all duration-300 ease-in-out h-full ${className}` },
        collapsed &&
            React.createElement(CollapsedContainer, { "$bgColor": backgroundColor },
                React.createElement(CollapsedHeader, { className: "f-border-b" },
                    React.createElement(CircleChevronRight, { className: "f-text cursor-pointer", size: 22, onClick: () => onToggleCollapse?.(false) })),
                React.createElement(CollapsedRail, null,
                    React.createElement(RailLine, { className: "f-bg-dark" }),
                    items.map((section, index) => {
                        const fields = Object.values((section.data?.value || {}));
                        const sectionName = section.data.type === 'section' ? section.data.fieldPath : 'main';
                        let sectionCompletedCount = fluidForm.getSectionCompletedFieldsCount(sectionName);
                        const sectionTotal = fields.length;
                        const sectionIsComplete = sectionTotal > 0 && sectionCompletedCount === sectionTotal;
                        return React.createElement(RailDot, { className: sectionIsComplete ? '' : 'fluid-primary-bg', key: index, "data-active": index === activeIndex, "data-completed": sectionIsComplete, shadowColor: primaryColor });
                    }))),
        !collapsed &&
            React.createElement(Container, { "$bgColor": backgroundColor },
                React.createElement(Header, { className: "f-border-b" },
                    React.createElement(Summary, { className: "f-text" },
                        "Field ",
                        completed,
                        " of ",
                        total),
                    React.createElement(ProgressBar, { className: "f-bg-dark", "aria-hidden": true },
                        React.createElement(ProgressFill, { className: "fluid-primary-bg", style: { width: `${percent}%` } })),
                    React.createElement(CloseButton, { type: "button", onClick: () => onToggleCollapse?.(true), "aria-label": "Collapse", "$bgColor": backgroundColor },
                        React.createElement(CircleChevronLeft, { className: "f-text cursor-pointer", size: 22 }))),
                React.createElement(SectionsList, { className: "overflow-y-auto" }, items.map((section, index) => {
                    const sectionLabel = section.title || `Section ${index + 1}`;
                    const fields = Object.values((section.data?.value || {}));
                    const sectionName = section.data.type === 'section' ? section.data.fieldPath : 'main';
                    let sectionCompletedCount = fluidForm.getSectionCompletedFieldsCount(sectionName);
                    const sectionTotal = fields.length;
                    const sectionIsComplete = sectionTotal > 0 && sectionCompletedCount === sectionTotal;
                    return (React.createElement(SectionItem, { key: index, "data-active": index === activeIndex, "data-complete": sectionIsComplete },
                        React.createElement(SectionRow, null,
                            React.createElement(TimelineColumn, null,
                                React.createElement(Badge, { className: sectionIsComplete ? 'f-bg-valid' : 'fluid-primary-bg', "data-complete": sectionIsComplete }, sectionIsComplete ? '✓' : index + 1),
                                React.createElement(Connector, null)),
                            React.createElement(SectionContent, null,
                                React.createElement(SectionHeader, { type: "button", onClick: () => fluidForm.setCurrentField(fields[0].fieldPath), "aria-current": index === activeIndex ? 'step' : undefined },
                                    React.createElement(SectionTitle, { className: "f-text" }, sectionLabel)),
                                sectionTotal > 0 && (React.createElement(FieldsList, null, fields.map((field, i) => (React.createElement(FieldItemComponent, { key: field.fieldPath || `${sectionName}-${i}`, field: field })))))))));
                }))));
}
function FieldItemComponent({ field }) {
    const fluidForm = useFluidForm();
    const fieldLabel = fluidForm.getFieldConfigByFieldPath(field.fieldPath)?.label || field.key;
    const validation = useFormInstanceSelect((state) => state.fluidForm.getFieldValidationResult(field.fieldPath));
    const [value, _] = useFormValue(field.fieldPath);
    const dataComplete = !isEmptyDeep(value);
    const validationType = validation ? validation.type : (dataComplete ? 'data-input' : null);
    return (React.createElement(FieldItem, { "data-complete": dataComplete },
        React.createElement(ValidationIconSmall, { size: 20, validationType: validationType }),
        React.createElement(FieldTitle, { className: "f-text-light", onClick: () => fluidForm.setCurrentField(field.fieldPath) }, fieldLabel)));
}
export default SectionsProgress;
// Styled components
const CollapsedContainer = styled.aside.attrs({ className: "f-border-r f-border-b" }) `
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background: ${props => props.$bgColor || '#ffffff'};
`;
const CollapsedHeader = styled.div `
  height: 40px;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 8px 6px;
  border-bottom: 1px solid #e5e7eb;
`;
const CollapsedHamburger = styled.div `
  width: 18px;
  display: grid;
  gap: 3px;
  cursor: pointer;
  margin: 0 auto;
  & > span {
    display: block;
    height: 2px;
    background: #111827;
    border-radius: 2px;
  }
`;
const CollapsedRail = styled.div `
  position: relative;
  flex: 1;
  display: flex;
  align-items: center;
  flex-direction: column;
  padding: 12px 0;
  gap: 24px;
`;
const RailLine = styled.div `
  position: absolute;
  top: 28px;
  bottom: 28px;
  width: 2px;
  background: #e5e7eb; /* gray-200 */
  z-index: 0
`;
const RailDot = styled.div `
  width: 14px;
  height: 14px;
  border-radius: 9999px;
  ${p => p['data-completed'] ? 'background: #22c55e' : ''}; /* green-500 or orange-500 */
  box-shadow: ${p => p['data-active'] ? (p['data-completed'] ? `0 0 0 3px #22c55e50` : `0 0 0 3px ${p['shadowColor']}50`) : 'none'};
  z-index: 1;
`;
const Container = styled.aside.attrs({ className: "f-border-r f-border-b" }) `
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  background: ${props => props.$bgColor || '#ffffff'};
`;
const Header = styled.header.attrs({ className: "shadow" }) `
  padding: 12px 16px;
  border-bottom: 1px solid #e5e7eb; /* gray-200 */
  position: relative;
  text-align: left;
  margin-bottom: 10px;
`;
const Title = styled.div `
  font-weight: 600;
  color: #1f2937; /* gray-800 */
  font-size: 12px;
`;
const Summary = styled.div `
  margin-top: 3px;
  color: #6b7280; /* gray-500 */
  font-size: 16px;
`;
const ProgressBar = styled.div `
  margin-top: 0px;
  height: 6px;
  border-radius: 9999px;
  background: #e5e7eb; /* gray-200 */
  overflow: hidden;
`;
const ProgressFill = styled.div `
  transition: width 0.3s ease;
  height: 100%;
  background: #9ca3af; /* gray-400 */
`;
const CloseButton = styled.button `
  position: absolute;
  top: 8px;
  right: 8px;
  width: 24px;
  height: 24px;
  border: none;
  background: ${props => props.$bgColor || '#ffffff'};
  border-radius: 6px;
  cursor: pointer;
  display: grid;
  place-items: center;
  padding: 0;

`;
const SectionsList = styled.ul `
  list-style: none;
  padding: 12px 0 16px 12px;
  margin: 0;
`;
const SectionItem = styled.li `
  margin-bottom: 18px;
  margin-right: 12px;
  &[data-active='true'] ${Title} {
    color: #111827; /* gray-900 */
  }
`;
const SectionRow = styled.div `
  display: flex;
  align-items: flex-start;
`;
const TimelineColumn = styled.div `
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-right: 12px;
`;
const Badge = styled.div `
  width: 24px;
  height: 24px;
  border-radius: 9999px;
  color: #ffffff;
  font-size: 12px;
  font-weight: 700;
  display: flex;
  align-items: center;
  justify-content: center;
`;
const Connector = styled.div `
  flex: 1;
  width: 1px;
  background: #e5e7eb; /* gray-200 */
  margin-top: 8px;
`;
const SectionContent = styled.div `
  flex: 1;
  min-width: 0;
`;
const SectionHeader = styled.button `
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 2px;
  background: transparent;
  border: 0;
  padding: 0;
  cursor: pointer;
`;
const SectionTitle = styled.div `
  font-size: 14px;
  font-weight: 600;
  color: #1f2937; /* gray-800 */
  text-align: left;
`;
const SectionMeta = styled.div `
  font-size: 12px;
  color: #6b7280; /* gray-500 */
`;
const FieldsList = styled.ul `
  list-style: none;
  margin: 8px 0 0 0;
`;
const FieldItem = styled.li `
  display: flex;
  align-items: center;
  gap: 8px;
  margin-top: 18px;
  color: ${p => p['data-complete'] ? '#111827' : '#6b7280'}; /* gray-900 or gray-500 */
`;
const FieldMarker = styled.span `
  width: 14px;
  height: 14px;
  border-radius: 9999px;
  background: #e5e7eb; /* gray-200 */
  display: inline-block;
`;
const FieldTitle = styled.span `
  font-size: 14px;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  cursor: pointer;
`;
